import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetPosOrderQuery,
  useGetPosOrderInvoicesQuery,
  useGetPosInvoiceSplitPreviewQuery,
  useProcessPosOrderPaymentMutation,
} from "../../store/api";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Modal from "../../components/common/Modal";
import Toast from "../../components/common/Toast";
import PaymentMethodSelector from "../../components/pos/PaymentMethodSelector";
import PrintReceiptAction from "../../components/pos/PrintReceiptAction";
import { getPosErrorMessage } from "../../utils/posErrorHandler";
import { useStationFeature } from "../../hooks/useStationFeature";
import { normalizeMoneyAmount } from "../../utils/money";

const formatMoney = (amount) => `${Number(amount || 0).toFixed(2)}`;

const getModifiersLabel = (modifiers) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";
  return modifiers
    .map(
      (modifier) =>
        modifier?.name ||
        modifier?.modifierName ||
        modifier?.optionName ||
        "Modifier",
    )
    .join(", ");
};

const SplitInvoice = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [locallyPaidPreviewKeys, setLocallyPaidPreviewKeys] = useState({});
  const [selectedTipAmount, setSelectedTipAmount] = useState(0);
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    subtotal: 0,
    taxTotal: 0,
    grandTotal: 0,
    paidItemIds: [],
  });
  const { tipConfig } = useStationFeature();

  const {
    data: order,
    isLoading: orderLoading,
    refetch: refetchOrder,
  } = useGetPosOrderQuery(orderId);

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useGetPosOrderInvoicesQuery(orderId);

  const [processOrderPayment, { isLoading: isProcessingPayment, error: paymentError }] =
    useProcessPosOrderPaymentMutation();

  const invoices = Array.isArray(invoicesData) ? invoicesData : [];
  const openInvoices = useMemo(
    () => invoices.filter((invoice) => invoice?.status !== "PAID"),
    [invoices],
  );
  const sourceInvoiceId = openInvoices[0]?.invoiceId;
  const {
    data: splitPreviewData,
    isLoading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useGetPosInvoiceSplitPreviewQuery(
    { orderId, sourceInvoiceId },
    { skip: !orderId },
  );
  const previewItems = Array.isArray(splitPreviewData?.items)
    ? splitPreviewData.items
    : [];

  const expandedItems = useMemo(() => {
    const items = [];

    previewItems.forEach((previewItem, index) => {
      const qty = Math.max(1, Number(previewItem?.qty || 1));
      const unitPrice = Number(previewItem?.unitPrice || 0);
      const unitSubtotal = Number(
        previewItem?.unitSubtotal !== undefined
          ? previewItem.unitSubtotal
          : unitPrice,
      );
      const unitTax = Number(previewItem?.unitTax || 0);
      const unitTotal = Number(
        previewItem?.unitTotal !== undefined
          ? previewItem.unitTotal
          : unitSubtotal + unitTax,
      );
      const baseKey =
        previewItem?.splitItemId ||
        `${previewItem?.orderLineId || "line"}-${previewItem?.itemId || "item"}-${index}`;

      for (let i = 0; i < qty; i++) {
        const unitId = `${baseKey}-${i}`;
        if (locallyPaidPreviewKeys[unitId]) continue;

        items.push({
          id: unitId,
          orderLineId: previewItem?.orderLineId,
          itemId: previewItem?.itemId,
          itemName: previewItem?.itemName || "Item",
          unitPrice,
          unitSubtotal,
          unitTax,
          unitTotal,
          modifiers: previewItem?.modifiers || [],
          notes: previewItem?.notes || "",
        });
      }
    });

    return items;
  }, [previewItems, locallyPaidPreviewKeys]);

  const isLoading = orderLoading || invoicesLoading || previewLoading;

  const totalPaid = invoices.reduce(
    (sum, inv) => sum + Number(inv.paidAmount || 0),
    0,
  );
  const orderGrandTotal = Number(order?.totals?.grandTotal || 0);
  const totalRemaining = orderGrandTotal - totalPaid;

  const {
    selectedSubTotal,
    selectedTax,
    selectedTotal,
    selectedCount,
    selectedItemsList,
  } = useMemo(() => {
    let subTotal = 0;
    let tax = 0;
    let total = 0;
    let count = 0;
    const itemsList = [];

    expandedItems.forEach((item) => {
      if (selectedItems[item.id]) {
        subTotal += item.unitSubtotal;
        tax += item.unitTax;
        total += item.unitTotal;
        count += 1;
        itemsList.push(item);
      }
    });

    return {
      selectedSubTotal: subTotal,
      selectedTax: tax,
      selectedTotal: total,
      selectedCount: count,
      selectedItemsList: itemsList,
    };
  }, [expandedItems, selectedItems]);

  const remainingAfterSelection = Math.max(
    totalRemaining - selectedTotal,
    0,
  );
  const modalAmountDue = Math.max(Number(paymentModal.grandTotal || 0), 0);
  const paymentTotalWithTip = Math.max(modalAmountDue + selectedTipAmount, 0);

  const handleItemToggle = (itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    expandedItems.forEach((item) => {
      allSelected[item.id] = true;
    });
    setSelectedItems(allSelected);
  };

  const handleClearSelection = () => {
    setSelectedItems({});
    setError(null);
    setToast(null);
  };

  const openPaymentModal = (
    { fallbackTotals = null, paidItemIds = [] } = {},
  ) => {
    if (!fallbackTotals) {
      setError("Payment details are unavailable. Please refresh and try again.");
      return;
    }

    const subtotal = Number(fallbackTotals?.subtotal || 0);
    const taxTotal = Number(fallbackTotals?.taxTotal || 0);
    const grandTotal = Number(fallbackTotals?.grandTotal || 0);

    setPaymentModal({
      isOpen: true,
      subtotal,
      taxTotal,
      grandTotal,
      paidItemIds,
    });
  };

  const handlePaySelected = async () => {
    setError(null);

    if (selectedCount === 0) {
      setError("Please select items to pay.");
      return;
    }

    const paidItemIds = Object.keys(selectedItems).filter(
      (itemId) => selectedItems[itemId],
    );
    const fallbackTotals = {
      subtotal: selectedSubTotal,
      taxTotal: selectedTax,
      grandTotal: selectedTotal,
    };
    openPaymentModal({ fallbackTotals, paidItemIds });
  };

  const closePaymentModal = () => {
    setSelectedTipAmount(0);
    setPaymentModal({
      isOpen: false,
      subtotal: 0,
      taxTotal: 0,
      grandTotal: 0,
      paidItemIds: [],
    });
  };

  const handleProcessPayment = async (method, amount, tipAmount = 0) => {
    setError(null);

    try {
      await processOrderPayment({
        orderId,
        payload: { method, amount: normalizeMoneyAmount(amount) },
      }).unwrap();

      if (paymentModal.paidItemIds?.length) {
        setLocallyPaidPreviewKeys((prev) => {
          const next = { ...prev };
          paymentModal.paidItemIds.forEach((itemId) => {
            next[itemId] = true;
          });
          return next;
        });
      }

      setPaymentModal({
        isOpen: false,
        subtotal: 0,
        taxTotal: 0,
        grandTotal: 0,
        paidItemIds: [],
      });
      setSelectedItems({});
      setToast({
        id: Date.now(),
        tone: "success",
        message: "Payment successful.",
      });

      await Promise.allSettled([refetchOrder(), refetchInvoices(), refetchPreview()]);
    } catch (err) {
      setError(getPosErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            Loading split preview...
          </p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Alert tone="error">Order not found</Alert>
      </div>
    );
  }

  if (previewError && !splitPreviewData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Alert tone="error">{getPosErrorMessage(previewError)}</Alert>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      )}

      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/pos/orders/${orderId}`)}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-base font-bold text-slate-900 dark:text-slate-100">
              Split Payment - {order.seatRef}
            </h1>
          </div>

          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Remaining
            </div>
            <div className="text-base font-bold text-rose-600 dark:text-rose-400">
              ${formatMoney(remainingAfterSelection)}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
          <Alert tone="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="w-[60%] border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
          <div className="border-b border-slate-200 dark:border-slate-700 p-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Items to Pay ({expandedItems.length} units)
            </h2>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleClearSelection}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {expandedItems.length === 0 ? (
              <div className="p-3">
                <Alert tone="info">No unpaid items available.</Alert>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {expandedItems.map((item) => {
                  const modifiersLabel = getModifiersLabel(item.modifiers);
                  const isSelected = Boolean(selectedItems[item.id]);

                  return (
                    <div
                      key={item.id}
                      className={`flex items-center gap-4 px-4 py-3 transition ${
                        isSelected
                          ? "bg-blue-50 dark:bg-blue-900/20"
                          : "bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {item.itemName}
                        </p>
                        {(modifiersLabel || item.notes) && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                            {modifiersLabel}
                            {modifiersLabel && item.notes ? " | " : ""}
                            {item.notes}
                          </p>
                        )}
                      </div>

                      <div className="text-right w-24">
                        <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                          ${formatMoney(item.unitTotal)}
                        </p>
                      </div>

                      <div className="w-10 flex justify-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleItemToggle(item.id)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 cursor-pointer rounded"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="w-[40%] bg-slate-50 dark:bg-slate-900 flex flex-col overflow-hidden border-l border-slate-200 dark:border-slate-700">
          <div className="flex-1 overflow-y-auto p-4">
            {/* Order Summary */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 mb-4 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-2">
                Order Summary
              </h2>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="text-center">
                  <div className="text-slate-500 dark:text-slate-400 mb-1">
                    Total
                  </div>
                  <div className="font-bold text-slate-900 dark:text-slate-100">
                    ${formatMoney(orderGrandTotal)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-slate-500 dark:text-slate-400 mb-1">
                    Paid
                  </div>
                  <div className="font-bold text-emerald-600 dark:text-emerald-400">
                    ${formatMoney(totalPaid)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-slate-500 dark:text-slate-400 mb-1">
                    Remaining
                  </div>
                  <div className="font-bold text-rose-600 dark:text-rose-400">
                    ${formatMoney(remainingAfterSelection)}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 mb-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
                  Invoice Receipts
                </h2>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Print each invoice independently
                </span>
              </div>

              <div className="space-y-2">
                {invoices.length === 0 && (
                  <Alert tone="info">No invoices available to print.</Alert>
                )}
                {invoices.map((invoice) => (
                  <div
                    key={invoice.invoiceId || invoice.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        #{invoice.invoiceNo || "Invoice"}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {invoice.status || "UNKNOWN"} | ${formatMoney(invoice.grandTotal)}
                      </div>
                    </div>
                    <PrintReceiptAction
                      invoiceId={invoice.invoiceId || invoice.id}
                      invoiceNo={invoice.invoiceNo}
                      variant="secondary"
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Items */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 mb-4 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400 mb-3">
                Selected Items ({selectedCount})
              </h2>

              {selectedCount === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                  No items selected
                </p>
              ) : (
                <>
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {selectedItemsList.map((item, idx) => (
                      <div
                        key={`${item.id}-${idx}`}
                        className="flex justify-between text-xs"
                      >
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1">
                          - {item.itemName}
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-slate-100 ml-2">
                          ${formatMoney(item.unitTotal)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-3 border-t border-blue-300 dark:border-blue-700">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Total to Pay
                      </span>
                      <span className="text-xl font-black text-blue-600 dark:text-blue-400">
                        ${formatMoney(selectedTotal)}
                      </span>
                    </div>

                    <Button
                      size="md"
                      onClick={handlePaySelected}
                      disabled={isProcessingPayment || selectedCount === 0}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {isProcessingPayment ? "Processing..." : "Pay Selected Items"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs">
            <span className="text-slate-500 dark:text-slate-400">
              Selected:{" "}
            </span>
            <span className="font-bold text-blue-600 dark:text-blue-400">
              {selectedCount} items (${formatMoney(selectedTotal)})
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/pos/orders/${orderId}`)}
            >
              Back to Order
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={paymentModal.isOpen}
        onClose={closePaymentModal}
        title="Payment"
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/80 dark:to-slate-800/70 p-4 shadow-sm">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600 dark:text-slate-400">Amount</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                ${formatMoney(paymentModal.subtotal)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600 dark:text-slate-400">Tax</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                ${formatMoney(paymentModal.taxTotal)}
              </span>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
              <span className="text-base font-semibold text-slate-700 dark:text-slate-300">
                Total Amount
              </span>
              <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400">
                ${formatMoney(paymentTotalWithTip)}
              </span>
            </div>
          </div>

          <PaymentMethodSelector
            key={`split-payment-${paymentModal.isOpen ? "open" : "closed"}`}
            totalAmount={modalAmountDue}
            onPay={handleProcessPayment}
            isLoading={isProcessingPayment}
            error={paymentError}
            compact
            showSummary={false}
            onTipChange={setSelectedTipAmount}
            tipConfig={tipConfig}
          />

          <div className="flex justify-end pt-1">
            <Button
              size="md"
              variant="secondary"
              onClick={closePaymentModal}
              disabled={isProcessingPayment}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SplitInvoice;

